import { useCallback, useEffect, useRef, useState } from "react";

// Web Locks hold leadership for the life of the audible tab. The lease fallback
// verifies ownership immediately before every tone and yields on storage events.
export function startAudioLeadership(name, onChange, environment = window) {
  let stopped = false,
    releaseLock,
    retry,
    settle;
  const id = `${Date.now()}-${Math.random()}`;
  const key = `duress-audio-leader:${name}`;
  let leader = false;
  const change = (value) => {
    if (leader !== value) {
      leader = value;
      onChange(value);
    }
  };
  const lease = () => {
    try {
      return JSON.parse(environment.localStorage.getItem(key) || "null");
    } catch {
      return null;
    }
  };
  const owns = () =>
    environment.navigator?.locks ? leader : leader && lease()?.id === id;
  const tick = async () => {
    if (stopped) return;
    if (environment.navigator?.locks) {
      try {
        await environment.navigator.locks.request(
          key,
          { ifAvailable: true },
          async (lock) => {
            if (!lock || stopped) return;
            change(true);
            await new Promise((resolve) => {
              releaseLock = resolve;
            });
            change(false);
          }
        );
      } catch {
        change(false);
      }
    } else {
      const current = lease();
      if (!current || current.id === id || current.expires < Date.now()) {
        try {
          environment.localStorage.setItem(
            key,
            JSON.stringify({ id, expires: Date.now() + 10000 })
          );
          settle = environment.setTimeout(() => {
            if (!stopped) change(lease()?.id === id);
          }, 150);
        } catch {
          change(false);
        } // No safe coordination: keep sound unavailable.
      } else change(false);
    }
    if (!stopped) retry = environment.setTimeout(tick, 2000);
  };
  const storage = () => {
    if (!environment.navigator?.locks && lease()?.id !== id) change(false);
  };
  const stop = () => {
    stopped = true;
    environment.clearTimeout(retry);
    environment.clearTimeout(settle);
    releaseLock?.();
    change(false);
    if (!environment.navigator?.locks && lease()?.id === id) {
      try {
        environment.localStorage.removeItem(key);
      } catch {}
    }
    environment.removeEventListener("storage", storage);
    environment.removeEventListener("pagehide", stop);
  };
  environment.addEventListener("storage", storage);
  environment.addEventListener("pagehide", stop);
  tick();
  return { stop, owns };
}
export default function useDuressAudio(alarmKey, scope) {
  const context = useRef(null),
    nodes = useRef([]),
    coordinator = useRef(null),
    lastPlayed = useRef(0);
  const [ready, setReady] = useState(false),
    [blocked, setBlocked] = useState(false),
    [leader, setLeader] = useState(false);
  const [volume, setVolume] = useState(() => {
    try {
      const saved = window.localStorage.getItem("duress-volume-v1");
      const value = saved === null ? 80 : Number(saved);
      return Number.isFinite(value) ? Math.min(100, Math.max(5, value)) : 80;
    } catch {
      return 80;
    }
  });
  const stopTone = useCallback(() => {
    nodes.current.forEach((node) => {
      try {
        node.stop();
      } catch {}
    });
    nodes.current = [];
  }, []);
  const play = useCallback(() => {
    const audio = context.current;
    if (!audio || audio.state !== "running") {
      setReady(false);
      setBlocked(true);
      return false;
    }
    stopTone();
    // Three high–low pairs form a short, assertive alarm. Square-wave harmonics
    // carry on small speakers; the attack/release avoids abrupt clicks. Leave
    // headroom and gaps between notes rather than adding overlapping voices.
    [0, 0.3, 0.6, 0.9, 1.2, 1.5].forEach((offset, index) => {
      const oscillator = audio.createOscillator(),
        gain = audio.createGain(),
        at = audio.currentTime + offset + 0.02,
        level = (volume / 100) * 0.45;
      oscillator.type = "square";
      oscillator.frequency.value = index % 2 ? 660 : 880;
      gain.gain.setValueAtTime(0, at);
      gain.gain.linearRampToValueAtTime(level, at + 0.015);
      gain.gain.setValueAtTime(level, at + 0.22);
      gain.gain.linearRampToValueAtTime(0, at + 0.26);
      oscillator.connect(gain);
      gain.connect(audio.destination);
      oscillator.start(at);
      oscillator.stop(at + 0.27);
      oscillator.onended = () => {
        oscillator.disconnect();
        gain.disconnect();
        nodes.current = nodes.current.filter((node) => node !== oscillator);
      };
      nodes.current.push(oscillator);
    });
    return true;
  }, [volume, stopTone]);
  const enable = useCallback(async () => {
    try {
      const Audio = window.AudioContext || window.webkitAudioContext;
      if (!Audio) throw new Error("Audio unavailable");
      if (!context.current || context.current.state === "closed")
        context.current = new Audio();
      await context.current.resume();
      if (context.current.state !== "running") throw new Error("Audio blocked");
      context.current.onstatechange = () => {
        const running = context.current?.state === "running";
        setReady(running);
        setBlocked(!running);
      };
      setReady(true);
      setBlocked(false);
      play();
    } catch {
      setReady(false);
      setBlocked(true);
    }
  }, [play]);
  useEffect(() => {
    try {
      window.localStorage.setItem("duress-volume-v1", String(volume));
    } catch {}
  }, [volume]);
  const hasAlarm = Boolean(alarmKey);
  // Claiming the last incident stops an active burst immediately. Keep this
  // separate from readiness so enabling/testing sound without an incident
  // does not immediately cancel the sample it just started.
  useEffect(() => {
    if (!hasAlarm) stopTone();
  }, [hasAlarm, stopTone]);
  useEffect(() => {
    if (!ready) {
      stopTone();
      return undefined;
    }
    if (!hasAlarm) return undefined;
    coordinator.current = startAudioLeadership(scope, setLeader);
    return () => {
      coordinator.current?.stop();
      coordinator.current = null;
      stopTone();
    };
  }, [ready, hasAlarm, scope, stopTone]);
  useEffect(() => {
    lastPlayed.current = 0;
  }, [alarmKey, leader]);
  useEffect(() => {
    if (!ready || !leader || !alarmKey) return undefined;
    const tick = () => {
      if (
        coordinator.current?.owns() &&
        Date.now() - lastPlayed.current >= 10000
      ) {
        if (play()) lastPlayed.current = Date.now();
      }
    };
    tick();
    const timer = window.setInterval(tick, 500);
    return () => {
      window.clearInterval(timer);
      stopTone();
    };
  }, [ready, leader, alarmKey, play, stopTone]);
  useEffect(
    () => () => {
      stopTone();
      if (context.current) {
        context.current.onstatechange = null;
        context.current.close().catch(() => {});
      }
    },
    [stopTone]
  );
  return {
    ready,
    blocked,
    leader,
    volume,
    setVolume,
    enable,
    label: !ready
      ? blocked
        ? "Alert sound blocked"
        : "Alert sound not enabled"
      : alarmKey && !leader
      ? "Sound handled by another tab"
      : "Alert sound ready",
  };
}
